import type {IssueComment} from '../../page-data/payloads/issue-comments'
import type {ReviewSummary} from '../../page-data/payloads/review-summaries'

/**
 * A tagged wrapper that identifies an item in the merged conversation list
 * as either an issue comment or a review summary, preserving the original type
 * for rendering with the appropriate component.
 */
export type ConversationItem =
  | {type: 'issue-comment'; data: IssueComment; sortDate: string}
  | {type: 'review-summary'; data: ReviewSummary; sortDate: string}

/**
 * Adapts a ReviewSummary to the IssueComment shape so it can be used
 * for filtering with useFilterIssueCommentsIds. The adapter maps:
 *  - submittedAt → createdAt / publishedAt
 *  - isHidden → false (review summaries are never hidden)
 *  - minimizedReason → null
 *  - repository is non-nullable on ReviewSummary, assignable to nullable
 */
export function reviewSummaryToIssueComment(summary: ReviewSummary): IssueComment {
  return {
    id: summary.id,
    authorAssociation: summary.authorAssociation,
    authorAvatarUrl: summary.authorAvatarUrl,
    authorLogin: summary.authorLogin,
    body: summary.body,
    bodyHtml: summary.bodyHtml,
    createdAt: summary.submittedAt,
    isHidden: false,
    minimizedReason: null,
    publishedAt: summary.submittedAt,
    reactionGroups: summary.reactionGroups,
    repository: summary.repository,
    url: summary.url,
    viewerCanReact: summary.viewerCanReact,
  }
}

/**
 * Builds a chronologically sorted list of ConversationItems from
 * issue comments and review summaries for display in the Conversation tab.
 */
export function buildConversationItems(
  issueComments: IssueComment[],
  reviewSummaries: ReviewSummary[],
): ConversationItem[] {
  const items: ConversationItem[] = [
    ...issueComments.map(
      (comment): ConversationItem => ({
        type: 'issue-comment',
        data: comment,
        sortDate: comment.createdAt,
      }),
    ),
    ...reviewSummaries.map(
      (summary): ConversationItem => ({
        type: 'review-summary',
        data: summary,
        sortDate: summary.submittedAt,
      }),
    ),
  ]
  return items.sort((a, b) => (a.sortDate < b.sortDate ? -1 : a.sortDate > b.sortDate ? 1 : 0))
}
